/**
 * SIL-477 — Reports tab locked copy. SIL-459 register R5/R6 discipline:
 * "consistent with", never "per"; no "Filed"/"assured"/"audited"/ISAE
 * reference; every framework row is framed as an export-ready extract the
 * customer files themselves — SiliconX never claims to file on the
 * customer's behalf.
 */
export interface DisclosureMappingRow {
  /** SIL-577 — the wire-contract key `dto/environmental-dashboard.ts`'s
   * `environmentalReportPackageFrameworkSchema` validates against. Purely
   * mechanical (no wording) — added alongside the existing `framework`/`note`
   * legal copy, never a rewording of it. */
  key: 'ghg_protocol' | 'csrd_esrs_e1' | 'cdp';
  framework: string;
  note: string;
}

export const DISCLOSURE_MAPPINGS: DisclosureMappingRow[] = [
  {
    key: 'ghg_protocol',
    framework: 'GHG Protocol',
    note: 'Asset-level Scope 2 (location-based) and Scope 3 Category 1 figures, exported for you to file directly in your own GHG inventory.',
  },
  {
    key: 'csrd_esrs_e1',
    framework: 'CSRD / ESRS E1',
    note: 'The same asset-level export maps to ESRS E1 datapoints; classifying it into your organizational boundary and consolidation approach remains your determination.',
  },
  {
    key: 'cdp',
    framework: 'CDP',
    note: 'The same asset-level export supports your own CDP Climate Change questionnaire response (C6/C7 modules) — SiliconX does not file on your behalf.',
  },
];

/** "Methodology & verification" card intro line — distinct from, and never
 * conflated with, the SIL-426 §14 scenario-forecast verification caveat
 * (`ESG_VERIFICATION_CAVEAT`, dto/esg-export.ts) — that string is scoped to
 * the Carbon Planning tool's forward-looking projections, this dashboard is
 * backward-looking historical estimates (SIL-459 R0). */
export const METHODOLOGY_CARD_INTRO =
  'Every figure on this dashboard and in its exports is computed consistent with the GHG Protocol, using location-based Scope 2 accounting. See the methodology sheet for the full pinned-container versions, scalar sources, and per-row basis vocabulary.';

/**
 * SIL-577 (I2-D5) — Export package composer card copy. ⚠ NEW user-facing
 * strings, none of them SIL-426/427 locked disclosure text — pending the
 * legal pass this ticket's issue body requires before merge. Written to the
 * SAME SIL-459 R5/R6 discipline as the rest of this file ("consistent
 * with", never "per"; no "filed"/"assured"/"audited") but that discipline
 * does not substitute for legal sign-off on NEW copy. Every string below,
 * plus the section labels rendered inline in `ReportPackageComposerCard.tsx`
 * ("Frameworks", "Sections", "Period", "Package contents"), is listed
 * verbatim in this ticket's implementation report for the legal pass.
 */
export const REPORT_PACKAGE_COMPOSER_TITLE = 'Export package';
export const REPORT_PACKAGE_COMPOSER_INTRO =
  "Choose which frameworks and sections to include, then download each file below - this composes your existing exports into a named package; it never creates new figures or a new methodology.";
export const REPORT_PACKAGE_PERIOD_LABEL = 'Current fleet snapshot';
/** SIL-577 legal vet fix: the prior wording ("every file reflects the fleet
 * as of its own 'data as of' date") was falsified by the periodized
 * scenario ESG exports and by the methodology sheet's null `asOfIso` — this
 * package composer's exports are not uniformly period-anchored, so the
 * note now scopes the "latest computed figures" claim to the fleet export
 * and calls out the methodology sheet as not period-based. */
export const REPORT_PACKAGE_PERIOD_NOTE =
  "The only period these reports export today - the fleet export reflects each asset's latest computed figures (see computed_at per row); the methodology sheet is not period-based.";
export const REPORT_PACKAGE_BUILD_BUTTON = 'Build package';
export const REPORT_PACKAGE_EMPTY_SELECTION =
  'Select at least one framework and one section to build a package.';
export const REPORT_PACKAGE_CONTENTS_FOOTNOTE =
  "Each file downloads unchanged from its own export — selecting more than one framework does not change a file's contents, only which framework(s) this package is intended to support.";
